import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import api from "../../lib/api";

async function fetchCategories() {
  const { data } = await api.get("/categories");
  return data;
}

async function fetchProducts(filters = {}, page = 1) {
  const { data } = await api.get("/products", { params: { ...filters, page } });
  return data; // { products, pagination }
}

async function fetchProductBySlug(slug) {
  const { data } = await api.get(`/products/${slug}`);
  return data;
}

async function fetchPriceRange(category) {
  const { data } = await api.get("/products/price-range", { params: category ? { category } : undefined });
  return data; // { min, max }
}

export function useCategories() {
  return useQuery({ queryKey: ["categories"], queryFn: fetchCategories });
}

// Non-paginated pages (Home's featured strip, category "shop by" grid)
// just want the first page's worth of results — this stays a plain
// query returning the flat product array, same shape callers already use.
export function useProducts(filters = {}) {
  return useQuery({
    queryKey: ["products", filters],
    queryFn: async () => (await fetchProducts(filters, 1)).products,
  });
}

// Storefront browsing (category pages, search) wants real pagination —
// each additional page is a separate network fetch, not a client-side
// slice of an ever-growing in-memory array.
export function useInfiniteProducts(filters = {}) {
  return useInfiniteQuery({
    queryKey: ["products", "infinite", filters],
    queryFn: ({ pageParam = 1 }) => fetchProducts(filters, pageParam),
    getNextPageParam: (lastPage) =>
      lastPage.pagination.page < lastPage.pagination.totalPages ? lastPage.pagination.page + 1 : undefined,
    initialPageParam: 1,
  });
}

// Bounds for the price slider — scoped to a category when given so the
// slider's range matches what's actually in view, not the whole catalog.
export function usePriceRange(category) {
  return useQuery({
    queryKey: ["products", "price-range", category ?? null],
    queryFn: () => fetchPriceRange(category),
    staleTime: 5 * 60 * 1000,
  });
}

export function useProduct(slug) {
  return useQuery({
    queryKey: ["product", slug],
    queryFn: () => fetchProductBySlug(slug),
    enabled: !!slug,
    // A missing slug is a 404, not a transient failure — retrying it
    // three times just delays the "not found" state the UI needs to show.
    retry: (failureCount, error) => error?.response?.status !== 404 && failureCount < 2,
  });
}
